/** Grouped roles for job search & setup Step 1 (client-side filter). */
export type RoleOption = {
  id: string;
  title: string;
  /** Polish job title as used in real Polish job listings. */
  titlePl: string;
  /** Extra tokens for search (lowercased when matching). */
  searchText?: string;
};

export type RoleCategory = {
  id: string;
  title: string;
  /** Polish category header. */
  titlePl: string;
  roles: RoleOption[];
};

export const ROLE_CATEGORIES: RoleCategory[] = [
  {
    id: 'engineering',
    title: 'Engineering',
    titlePl: 'Inżynieria',
    roles: [
      { id: 'swe', title: 'Software Engineer', titlePl: 'Inżynier Oprogramowania', searchText: 'developer programmer coding' },
      { id: 'frontend', title: 'Frontend Developer', titlePl: 'Programista Frontend', searchText: 'react ui web spa' },
      { id: 'backend', title: 'Backend Developer', titlePl: 'Programista Backend', searchText: 'api server node java go' },
      { id: 'fullstack', title: 'Full Stack Developer', titlePl: 'Programista Full Stack', searchText: 'fullstack end to end web' },
      { id: 'mobile', title: 'Mobile Engineer', titlePl: 'Inżynier Mobile', searchText: 'ios android flutter react native' },
      { id: 'devops', title: 'DevOps / SRE', titlePl: 'DevOps / SRE', searchText: 'kubernetes aws ci cd reliability' },
      { id: 'platform', title: 'Platform Engineer', titlePl: 'Inżynier Platformy', searchText: 'internal developer platform tooling' },
      { id: 'cloud', title: 'Cloud Engineer', titlePl: 'Inżynier Chmury', searchText: 'aws azure gcp infrastructure' },
      { id: 'data-eng', title: 'Data Engineer', titlePl: 'Inżynier Danych', searchText: 'etl spark pipeline warehouse' },
      { id: 'embedded', title: 'Embedded Engineer', titlePl: 'Inżynier Embedded', searchText: 'firmware iot c rtos hardware' },
      { id: 'security-eng', title: 'Security Engineer', titlePl: 'Inżynier Bezpieczeństwa', searchText: 'appsec infosec secure coding' },
      { id: 'solutions-arch', title: 'Solutions Architect', titlePl: 'Architekt Rozwiązań', searchText: 'architecture integration presales' },
    ],
  },
  {
    id: 'product-design',
    title: 'Product & Design',
    titlePl: 'Produkt i Design',
    roles: [
      { id: 'pm', title: 'Product Manager', titlePl: 'Kierownik Produktu', searchText: 'roadmap discovery agile strategy' },
      { id: 'po', title: 'Product Owner', titlePl: 'Product Owner', searchText: 'backlog scrum stakeholder' },
      { id: 'product-designer', title: 'Product Designer', titlePl: 'Product Designer', searchText: 'end to end ux ui flows' },
      { id: 'ux', title: 'UX Designer', titlePl: 'Projektant UX', searchText: 'figma research usability journeys' },
      { id: 'ui', title: 'UI Designer', titlePl: 'Projektant UI', searchText: 'visual brand design system' },
      { id: 'ux-researcher', title: 'UX Researcher', titlePl: 'Researcher UX', searchText: 'user interviews usability testing' },
      { id: 'service-designer', title: 'Service Designer', titlePl: 'Projektant Usług', searchText: 'service blueprint journey mapping' },
    ],
  },
  {
    id: 'data-ml',
    title: 'Data & ML',
    titlePl: 'Dane i ML',
    roles: [
      { id: 'ds', title: 'Data Scientist', titlePl: 'Data Scientist', searchText: 'model statistics python experiment' },
      { id: 'ml', title: 'Machine Learning Engineer', titlePl: 'Inżynier Machine Learning', searchText: 'pytorch tensorflow nlp cv' },
      { id: 'ai-eng', title: 'AI Engineer', titlePl: 'Inżynier AI', searchText: 'llm genai prompt rag agents' },
      { id: 'mlops', title: 'MLOps Engineer', titlePl: 'Inżynier MLOps', searchText: 'model deployment monitoring ml pipeline' },
      { id: 'analyst', title: 'Data Analyst', titlePl: 'Analityk Danych', searchText: 'sql tableau bi reporting' },
      { id: 'analytics-eng', title: 'Analytics Engineer', titlePl: 'Inżynier Analityki', searchText: 'dbt warehouse metrics semantic layer' },
      { id: 'bi-dev', title: 'BI Developer', titlePl: 'Developer BI', searchText: 'power bi looker dashboards etl' },
    ],
  },
  {
    id: 'qa',
    title: 'Quality Assurance',
    titlePl: 'Quality Assurance',
    roles: [
      { id: 'qa-eng', title: 'QA Engineer', titlePl: 'Inżynier QA', searchText: 'manual testing quality regression' },
      { id: 'sdet', title: 'SDET', titlePl: 'SDET', searchText: 'test automation selenium cypress' },
      { id: 'test-automation', title: 'Test Automation Engineer', titlePl: 'Inżynier Automatyzacji Testów', searchText: 'playwright ci test framework' },
      { id: 'qa-lead', title: 'QA Lead', titlePl: 'Lider QA', searchText: 'test strategy quality lead' },
    ],
  },
  {
    id: 'security',
    title: 'Cybersecurity',
    titlePl: 'Cyberbezpieczeństwo',
    roles: [
      { id: 'sec-analyst', title: 'Security Analyst', titlePl: 'Analityk Bezpieczeństwa', searchText: 'threat vulnerability monitoring' },
      { id: 'appsec', title: 'Application Security Engineer', titlePl: 'Inżynier Application Security', searchText: 'owasp sast dast code review' },
      { id: 'soc-analyst', title: 'SOC Analyst', titlePl: 'Analityk SOC', searchText: 'siem incident response soc' },
      { id: 'pentester', title: 'Penetration Tester', titlePl: 'Tester Penetracyjny', searchText: 'pentest red team offensive' },
    ],
  },
  {
    id: 'leadership',
    title: 'Leadership',
    titlePl: 'Stanowiska Kierownicze',
    roles: [
      { id: 'tech-lead', title: 'Tech Lead', titlePl: 'Tech Lead', searchText: 'architecture mentor code review' },
      { id: 'staff-eng', title: 'Staff Engineer', titlePl: 'Staff Engineer', searchText: 'staff principal technical leadership' },
      { id: 'principal-eng', title: 'Principal Engineer', titlePl: 'Principal Engineer', searchText: 'principal architect deep expert' },
      { id: 'em', title: 'Engineering Manager', titlePl: 'Kierownik Zespołu Inżynierskiego', searchText: 'people lead team manager' },
      { id: 'eng-director', title: 'Director of Engineering', titlePl: 'Dyrektor Inżynierii', searchText: 'director org multi team' },
      { id: 'head-product', title: 'Head of Product', titlePl: 'Head of Product', searchText: 'product leadership portfolio' },
      { id: 'cto', title: 'CTO / VP Engineering', titlePl: 'CTO / VP Engineering', searchText: 'executive strategy cto vp' },
    ],
  },
  {
    id: 'business',
    title: 'Business & Operations',
    titlePl: 'Biznes i Operacje',
    roles: [
      { id: 'ba', title: 'Business Analyst', titlePl: 'Analityk Biznesowy', searchText: 'requirements process stakeholder' },
      { id: 'project-mgr', title: 'Project Manager', titlePl: 'Kierownik Projektu', searchText: 'project delivery timeline budget' },
      { id: 'program-mgr', title: 'Program Manager', titlePl: 'Kierownik Programu', searchText: 'program portfolio cross project' },
      { id: 'scrum-master', title: 'Scrum Master', titlePl: 'Scrum Master', searchText: 'scrum agile facilitation ceremonies' },
      { id: 'ops-mgr', title: 'Operations Manager', titlePl: 'Kierownik Operacji', searchText: 'operations efficiency process' },
      { id: 'consultant', title: 'Management Consultant', titlePl: 'Konsultant Zarządzania', searchText: 'consulting strategy advisory' },
    ],
  },
  {
    id: 'marketing-sales',
    title: 'Marketing & Sales',
    titlePl: 'Marketing i Sprzedaż',
    roles: [
      { id: 'marketing-mgr', title: 'Marketing Manager', titlePl: 'Kierownik Marketingu', searchText: 'campaign brand go to market' },
      { id: 'content-marketing', title: 'Content Marketing Specialist', titlePl: 'Specjalista Content Marketingu', searchText: 'content blog copywriting seo' },
      { id: 'growth', title: 'Growth Marketing Manager', titlePl: 'Kierownik Growth Marketingu', searchText: 'growth funnel acquisition retention' },
      { id: 'seo-specialist', title: 'SEO Specialist', titlePl: 'Specjalista SEO', searchText: 'seo sem search ranking' },
      { id: 'sales-rep', title: 'Sales Representative', titlePl: 'Przedstawiciel Handlowy', searchText: 'sales quota pipeline closing' },
      { id: 'account-exec', title: 'Account Executive', titlePl: 'Account Executive', searchText: 'ae enterprise deals closing' },
      { id: 'sdr', title: 'SDR / BDR', titlePl: 'SDR / BDR', searchText: 'outbound prospecting lead generation' },
      { id: 'customer-marketing', title: 'Customer Marketing Manager', titlePl: 'Kierownik Marketingu Klientów', searchText: 'customer advocacy case studies' },
    ],
  },
  {
    id: 'finance',
    title: 'Finance & Accounting',
    titlePl: 'Finanse i Księgowość',
    roles: [
      { id: 'financial-analyst', title: 'Financial Analyst', titlePl: 'Analityk Finansowy', searchText: 'financial analysis modeling reporting' },
      { id: 'fpa', title: 'FP&A Analyst', titlePl: 'Analityk FP&A', searchText: 'budget forecast planning variance' },
      { id: 'accountant', title: 'Accountant', titlePl: 'Księgowy', searchText: 'accounting ledger reconciliation' },
      { id: 'controller', title: 'Financial Controller', titlePl: 'Kontroler Finansowy', searchText: 'controller close gaap ifrs' },
    ],
  },
  {
    id: 'people',
    title: 'HR & People',
    titlePl: 'HR i People',
    roles: [
      { id: 'hr-bp', title: 'HR Business Partner', titlePl: 'HR Business Partner', searchText: 'hrbp employee relations policy' },
      { id: 'recruiter', title: 'Recruiter', titlePl: 'Rekruter', searchText: 'talent sourcing hiring interviews' },
      { id: 'talent-acquisition', title: 'Talent Acquisition Specialist', titlePl: 'Specjalista Talent Acquisition', searchText: 'ta sourcing employer branding' },
      { id: 'people-ops', title: 'People Operations Specialist', titlePl: 'Specjalista People Operations', searchText: 'people ops hr processes onboarding' },
    ],
  },
  {
    id: 'customer',
    title: 'Customer Success & Support',
    titlePl: 'Customer Success i Wsparcie',
    roles: [
      { id: 'cs-mgr', title: 'Customer Success Manager', titlePl: 'Customer Success Manager', searchText: 'csm retention expansion onboarding' },
      { id: 'support-spec', title: 'Customer Support Specialist', titlePl: 'Specjalista Wsparcia Klienta', searchText: 'support tickets helpdesk customer' },
      { id: 'tech-support', title: 'Technical Support Engineer', titlePl: 'Inżynier Wsparcia Technicznego', searchText: 'technical support troubleshooting tier' },
      { id: 'implementation', title: 'Implementation Specialist', titlePl: 'Specjalista Wdrożeń', searchText: 'implementation onboarding integration client' },
    ],
  },
  {
    id: 'it',
    title: 'IT & Infrastructure',
    titlePl: 'IT i Infrastruktura',
    roles: [
      { id: 'sysadmin', title: 'System Administrator', titlePl: 'Administrator Systemów', searchText: 'windows linux servers admin' },
      { id: 'network-eng', title: 'Network Engineer', titlePl: 'Inżynier Sieci', searchText: 'network cisco routing switching' },
      { id: 'it-support', title: 'IT Support Specialist', titlePl: 'Specjalista Wsparcia IT', searchText: 'helpdesk desktop support it' },
      { id: 'sap-consultant', title: 'SAP Consultant', titlePl: 'Konsultant SAP', searchText: 'sap erp implementation module' },
    ],
  },
  {
    id: 'healthcare',
    title: 'Healthcare & Medical',
    titlePl: 'Ochrona Zdrowia',
    roles: [
      { id: 'nurse', title: 'Registered Nurse', titlePl: 'Pielęgniarka / Pielęgniarz', searchText: 'nursing patient care hospital clinic' },
      { id: 'physician', title: 'Physician / Medical Doctor', titlePl: 'Lekarz', searchText: 'doctor medicine diagnosis treatment' },
      { id: 'nurse-practitioner', title: 'Nurse Practitioner', titlePl: 'Pielęgniarka / Pielęgniarz Specjalista', searchText: 'np advanced practice nursing' },
      { id: 'pharmacist', title: 'Pharmacist', titlePl: 'Farmaceuta', searchText: 'pharmacy medication dispensing' },
      { id: 'medical-assistant', title: 'Medical Assistant', titlePl: 'Asystent Medyczny', searchText: 'clinic patient intake vitals' },
      { id: 'physiotherapist', title: 'Physical Therapist', titlePl: 'Fizjoterapeuta', searchText: 'rehabilitation mobility therapy' },
      { id: 'healthcare-admin', title: 'Healthcare Administrator', titlePl: 'Administrator Ochrony Zdrowia', searchText: 'hospital operations medical admin' },
    ],
  },
  {
    id: 'education',
    title: 'Education & Training',
    titlePl: 'Edukacja i Szkolenia',
    roles: [
      { id: 'teacher', title: 'Teacher / Educator', titlePl: 'Nauczyciel / Edukator', searchText: 'school classroom teaching curriculum' },
      { id: 'lecturer', title: 'University Lecturer', titlePl: 'Wykładowca Akademicki', searchText: 'university academic lecture research' },
      { id: 'school-counselor', title: 'School Counselor', titlePl: 'Pedagog Szkolny', searchText: 'student guidance counseling school' },
      { id: 'instructional-designer', title: 'Instructional Designer', titlePl: 'Projektant Szkoleń', searchText: 'learning curriculum training materials' },
      { id: 'tutor', title: 'Tutor', titlePl: 'Korepetytor', searchText: 'tutoring one on one learning support' },
      { id: 'training-specialist', title: 'Training Specialist', titlePl: 'Specjalista ds. Szkoleń', searchText: 'corporate training workshops l&d' },
    ],
  },
  {
    id: 'legal',
    title: 'Legal & Compliance',
    titlePl: 'Prawo i Compliance',
    roles: [
      { id: 'lawyer', title: 'Lawyer / Attorney', titlePl: 'Prawnik / Adwokat', searchText: 'legal counsel litigation contracts' },
      { id: 'paralegal', title: 'Paralegal', titlePl: 'Asystent Prawny', searchText: 'legal research documentation filings' },
      { id: 'compliance-officer', title: 'Compliance Officer', titlePl: 'Compliance Officer', searchText: 'regulatory compliance audit policy' },
      { id: 'legal-counsel', title: 'In-house Legal Counsel', titlePl: 'Radca Prawny', searchText: 'in house legal corporate contracts' },
      { id: 'contract-mgr', title: 'Contract Manager', titlePl: 'Kierownik ds. Umów', searchText: 'contracts negotiation vendor agreements' },
    ],
  },
  {
    id: 'creative',
    title: 'Creative & Media',
    titlePl: 'Kreatywność i Media',
    roles: [
      { id: 'graphic-designer', title: 'Graphic Designer', titlePl: 'Grafik', searchText: 'graphics print branding layout' },
      { id: 'video-editor', title: 'Video Editor', titlePl: 'Montażysta Wideo', searchText: 'video editing post production' },
      { id: 'photographer', title: 'Photographer', titlePl: 'Fotograf', searchText: 'photography studio commercial portrait' },
      { id: 'journalist', title: 'Journalist / Reporter', titlePl: 'Dziennikarz / Reporter', searchText: 'news reporting writing media' },
      { id: 'social-media-mgr', title: 'Social Media Manager', titlePl: 'Social Media Manager', searchText: 'social media content community' },
      { id: 'brand-mgr', title: 'Brand Manager', titlePl: 'Brand Manager', searchText: 'brand strategy positioning identity' },
      { id: 'pr-specialist', title: 'Public Relations Specialist', titlePl: 'Specjalista ds. PR', searchText: 'public relations press communications' },
    ],
  },
  {
    id: 'hospitality',
    title: 'Hospitality & Tourism',
    titlePl: 'Hotelarstwo i Turystyka',
    roles: [
      { id: 'hotel-mgr', title: 'Hotel Manager', titlePl: 'Kierownik Hotelu', searchText: 'hotel hospitality guest operations' },
      { id: 'chef', title: 'Chef / Cook', titlePl: 'Kucharz / Szef Kuchni', searchText: 'kitchen culinary food preparation' },
      { id: 'restaurant-mgr', title: 'Restaurant Manager', titlePl: 'Kierownik Restauracji', searchText: 'restaurant dining service f&b' },
      { id: 'event-planner', title: 'Event Planner', titlePl: 'Organizator Wydarzeń', searchText: 'events coordination venues logistics' },
      { id: 'travel-agent', title: 'Travel Agent', titlePl: 'Agent Turystyczny', searchText: 'travel bookings tours itineraries' },
      { id: 'receptionist', title: 'Receptionist', titlePl: 'Recepcjonista', searchText: 'front desk reception guest welcome' },
    ],
  },
  {
    id: 'retail',
    title: 'Retail & E-commerce',
    titlePl: 'Handel i E-commerce',
    roles: [
      { id: 'store-mgr', title: 'Retail Store Manager', titlePl: 'Kierownik Sklepu', searchText: 'retail store sales team inventory' },
      { id: 'merchandiser', title: 'Merchandiser', titlePl: 'Merchandiser', searchText: 'merchandising product placement retail' },
      { id: 'ecommerce-mgr', title: 'E-commerce Manager', titlePl: 'Kierownik E-commerce', searchText: 'online store marketplace conversion' },
      { id: 'buyer', title: 'Buyer / Purchasing Specialist', titlePl: 'Buyer / Specjalista ds. Zakupów', searchText: 'buying procurement assortment vendor' },
      { id: 'visual-merchandiser', title: 'Visual Merchandiser', titlePl: 'Visual Merchandiser', searchText: 'visual display store layout retail' },
    ],
  },
  {
    id: 'manufacturing',
    title: 'Manufacturing & Logistics',
    titlePl: 'Produkcja i Logistyka',
    roles: [
      { id: 'production-supervisor', title: 'Production Supervisor', titlePl: 'Brygadzista / Kierownik Produkcji', searchText: 'production floor manufacturing shift' },
      { id: 'quality-inspector', title: 'Quality Inspector', titlePl: 'Kontroler Jakości', searchText: 'quality control inspection standards' },
      { id: 'supply-chain', title: 'Supply Chain Coordinator', titlePl: 'Koordynator Łańcucha Dostaw', searchText: 'supply chain inventory planning' },
      { id: 'warehouse-supervisor', title: 'Warehouse Supervisor', titlePl: 'Kierownik Magazynu', searchText: 'warehouse inventory picking shipping' },
      { id: 'logistics-coord', title: 'Logistics Coordinator', titlePl: 'Koordynator Logistyki', searchText: 'logistics freight transport routing' },
      { id: 'procurement', title: 'Procurement Specialist', titlePl: 'Specjalista ds. Zaopatrzenia', searchText: 'procurement sourcing supplier contracts' },
    ],
  },
  {
    id: 'real-estate',
    title: 'Real Estate & Construction',
    titlePl: 'Nieruchomości i Budownictwo',
    roles: [
      { id: 'real-estate-agent', title: 'Real Estate Agent', titlePl: 'Agent Nieruchomości', searchText: 'real estate property sales leasing' },
      { id: 'construction-pm', title: 'Construction Project Manager', titlePl: 'Kierownik Projektu Budowlanego', searchText: 'construction site building project' },
      { id: 'civil-engineer', title: 'Civil Engineer', titlePl: 'Inżynier Budownictwa', searchText: 'civil engineering structures infrastructure' },
      { id: 'architect', title: 'Architect', titlePl: 'Architekt', searchText: 'architecture building design blueprints' },
      { id: 'property-mgr', title: 'Property Manager', titlePl: 'Zarządca Nieruchomości', searchText: 'property management tenants maintenance' },
    ],
  },
  {
    id: 'public-sector',
    title: 'Public & Social Sector',
    titlePl: 'Sektor Publiczny i Społeczny',
    roles: [
      { id: 'social-worker', title: 'Social Worker', titlePl: 'Pracownik Socjalny', searchText: 'social work community support case' },
      { id: 'policy-analyst', title: 'Policy Analyst', titlePl: 'Analityk Polityk Publicznych', searchText: 'public policy research government' },
      { id: 'ngo-coordinator', title: 'NGO Program Coordinator', titlePl: 'Koordynator Programu NGO', searchText: 'nonprofit program community outreach' },
      { id: 'community-mgr', title: 'Community Manager', titlePl: 'Community Manager', searchText: 'community engagement events members' },
    ],
  },
  {
    id: 'general-other',
    title: 'General & Other',
    titlePl: 'Ogólne i Inne',
    roles: [
      { id: 'admin-assistant', title: 'Administrative Assistant', titlePl: 'Asystent / Asystentka Biurowa', searchText: 'admin office scheduling correspondence' },
      { id: 'office-mgr', title: 'Office Manager', titlePl: 'Office Manager', searchText: 'office operations facilities admin' },
      { id: 'exec-assistant', title: 'Executive Assistant', titlePl: 'Asystent / Asystentka Zarządu', searchText: 'executive support calendar travel' },
      { id: 'general-mgr', title: 'General Manager', titlePl: 'Dyrektor Generalny', searchText: 'general management p&l operations' },
      { id: 'team-lead', title: 'Team Lead', titlePl: 'Team Leader', searchText: 'team lead supervisor coordination' },
      { id: 'customer-service', title: 'Customer Service Representative', titlePl: 'Specjalista Obsługi Klienta', searchText: 'customer service phone email support' },
      { id: 'intern', title: 'Intern / Graduate Trainee', titlePl: 'Stażysta / Absolwent', searchText: 'intern graduate trainee entry level' },
      { id: 'other', title: 'Other Role', titlePl: 'Inne Stanowisko', searchText: 'other custom general role' },
    ],
  },
];

export function flattenRoles(): RoleOption[] {
  return ROLE_CATEGORIES.flatMap((c) => c.roles);
}

/**
 * Polish title for an English-canonical catalog role, or null when the role is
 * not in the catalog (AI-extracted / freely typed roles).
 */
export function findCatalogRolePl(roleEn: string): string | null {
  const match = ROLE_CATEGORIES.flatMap((c) => c.roles).find((r) => r.title === roleEn);
  return match?.titlePl ?? null;
}

/**
 * Picks the role label to show for the given UI language. Roles are stored
 * English-canonical everywhere (matching, analytics, AI prompts); `rolePl` is
 * the translation captured at creation time, so display falls back to English
 * whenever it is missing.
 */
export function resolveRoleLabel(
  role: { role?: string | null; rolePl?: string | null } | null | undefined,
  language: string | undefined,
): string {
  if (!role?.role) return '';
  if (!language?.toLowerCase().startsWith('pl')) return role.role;
  return role.rolePl || findCatalogRolePl(role.role) || role.role;
}

/** Stable sort index for skill grouping in setup UI (unknown ids sort last). */
export function roleIdSortIndex(roleId: string): number {
  if (roleId === 'general') return Number.MAX_SAFE_INTEGER;
  let index = 0;
  for (const cat of ROLE_CATEGORIES) {
    for (const role of cat.roles) {
      if (role.id === roleId) return index;
      index += 1;
    }
  }
  return Number.MAX_SAFE_INTEGER - 1;
}
